import { cloneDeep } from 'lodash';
import { useCallback } from 'react';
import isEqual from 'react-fast-compare';
import { useDispatch } from 'react-redux';

import { type EntityLookupResponse } from '@air/api';
import { type BaseCustomField } from '@air/api/types';

import { useApplyFilters } from '~/hooks/filters/useApplyFilters';
import { useFilterParams } from '~/hooks/filters/useFilterParams';
import { useFiltersContext } from '~/providers/FiltersProvider';
import { setFiltersValuesAction, setSelectedFiltersAction } from '~/store/filters/actions';
import { filtersValuesSelector, selectedFiltersSelector } from '~/store/filters/selectors';
import {
  type CustomFieldSelectedFilter,
  type Filters,
  initialFiltersState,
  type SelectedFilter,
} from '~/store/filters/types';
import { type FilterLookupsOptions } from '~/swr-hooks/filters/types';
import { getExcludedFiltersFromImmutableFilters } from '~/utils/filters/fastFiltersUtils';
import { getUrlParamsFromFilters } from '~/utils/filters/filtersToUrlParamsUtils';
import { customFieldFilterIsActive, customFieldUrlParamNeedsCatalog } from '~/utils/filters/plainTextCustomFieldFilter';
import {
  customFieldsHydratedWithFailedCatalog,
  getAssetFileKindStateFromParams,
  getColorsStateFromParams,
  getCustomFieldsStateFromParams,
  getDateStateFromParams,
  getImportedKeywordsStateFromParams,
  getLocationStateFromParams,
  getLocationsStateFromParams,
  getOthersStateFromParams,
  getPeopleStateFromParams,
  getSmartTagsStateFromParams,
  getTagsStateFromParams,
  getTypesStateFromParams,
  getUploadersStateFromParams,
  mergeCustomFieldsForHydration,
  plainTextSelectionNeedsCatalog,
  selectedFiltersForUnresolvedPlainText,
} from '~/utils/filters/urlParamsToFiltersUtils';
import { isDefined } from '~/utils/isDefined';
import { useAirStore } from '~/utils/ReduxUtils';

export const useSetFiltersFromContextToRedux = ({
  entities,
  options,
  catalogCustomFields,
  preserveUnresolvedPlainText = false,
}: {
  options?: FilterLookupsOptions;
  entities?: EntityLookupResponse;
  catalogCustomFields: BaseCustomField[] | undefined;
  preserveUnresolvedPlainText?: boolean;
}) => {
  const dispatch = useDispatch();
  const filters = useFilterParams();

  const store = useAirStore();

  const { applyFilters } = useApplyFilters();

  const { filters: filtersParams, immutableFilters = {} } = useFiltersContext();

  const setFiltersFromContextToRedux = useCallback(() => {
    if (!entities || !options) {
      return;
    }

    const hasCustomFieldCatalogUrlParam = Object.keys(filtersParams.customFields ?? {}).some(
      customFieldUrlParamNeedsCatalog,
    );

    if (hasCustomFieldCatalogUrlParam && catalogCustomFields === undefined) {
      return;
    }

    const catalogFields = catalogCustomFields ?? [];

    const { uploaders: uploadersOptions = [] } = options;
    const { customFields: customFieldsOptions = [], tags: tagsOptions = [], people: peopleOptions = [] } = entities;

    const currentFilters = filtersValuesSelector(store.getState());
    const currentUrlParams = getUrlParamsFromFilters(currentFilters);

    const { customFields: urlCustomFields, ...restUrlParams } = filtersParams;
    const filtersUrlParams = {
      ...restUrlParams,
      ...urlCustomFields,
    };

    if (isEqual(filtersUrlParams, currentUrlParams)) {
      const selectedCustomFields = selectedFiltersSelector(store.getState()).flatMap((filter) =>
        filter.customField
          ? [
              {
                id: filter.customField.id,
                name: filter.customField.name,
                unresolvedPlainText: filter.unresolvedPlainText,
              },
            ]
          : [],
      );

      if (
        !plainTextSelectionNeedsCatalog({
          catalogFields: catalogCustomFields ?? [],
          customFields: currentFilters.customFields,
          selectedCustomFields,
        })
      ) {
        return;
      }
    }

    const { excludedOtherFilters, excludedFilterTypes, excludedCustomFields } =
      getExcludedFiltersFromImmutableFilters(immutableFilters);

    const { selectedFilters, ...filtersState } = cloneDeep(initialFiltersState);
    const newSelectedFilters: SelectedFilter[] = selectedFilters;
    const newFiltersState: Filters = filtersState;

    const tags = getTagsStateFromParams(filters.tagsParam, tagsOptions);
    const tagsAreMissing = tags.tags.some((tag) => !tag);

    if (tagsAreMissing) {
      tags.tags = tags.tags.filter((tag) => !!tag);
    }
    if (!excludedFilterTypes.has('tags') && tags.tags.length) {
      newSelectedFilters.push({ type: 'tags' });
      newFiltersState.tags = tags;
    }

    const people = getPeopleStateFromParams(filters.peopleParam, peopleOptions);
    const peopleAreMissing = people.people.some((person) => !person);

    if (peopleAreMissing) {
      people.people = people.people.filter((person) => !!person);
    }
    if (!excludedFilterTypes.has('people') && people.people.length) {
      newSelectedFilters.push({ type: 'people' });
      newFiltersState.people = people;
    }

    let uploaders = getUploadersStateFromParams(filters.uploadersParam, uploadersOptions);
    const uploadersAreMissing = uploaders.some((uploader) => !uploader);

    if (uploadersAreMissing) {
      uploaders = uploaders.filter((uploader) => !!uploader);
    }
    if (!excludedFilterTypes.has('uploader') && uploaders?.length) {
      newSelectedFilters.push({ type: 'uploader' });
      newFiltersState.uploaders = uploaders;
    }

    if (!excludedFilterTypes.has('color') || !excludedFilterTypes.has('colorThreshold')) {
      const colorsParams = getColorsStateFromParams(filters.colorsParam);

      if (colorsParams.colors.length > 0) {
        newSelectedFilters.push({ type: 'color' });
        newFiltersState.colors = colorsParams;
      }
    }

    const others = getOthersStateFromParams({
      bookmarked: excludedOtherFilters.has('is-favorited') ? undefined : filters.bookmarkedParam,
      hasVersions: excludedOtherFilters.has('has-versions') ? undefined : filters.hasVersionsParam,
      hasComments: excludedOtherFilters.has('has-open-comments') ? undefined : filters.hasOpenCommentsParam,
      untagged: excludedOtherFilters.has('is-untagged') ? undefined : filters.untaggedParam,
      isOnBoards: excludedOtherFilters.has('is-on-boards') ? undefined : filters.isOnBoardsParam,
    });

    if (others?.length) {
      newSelectedFilters.push({ type: 'other' });
      newFiltersState.others = others;
    }

    const types = getTypesStateFromParams(filters.typesParam);
    const assetFileKind = getAssetFileKindStateFromParams(filters.assetFileKindParam);

    if (types?.length || assetFileKind?.length) {
      newSelectedFilters.push({ type: 'type' });
      newFiltersState.types = types;
      newFiltersState.assetFileKind = assetFileKind;
    }

    const sources = filters.sourcesParam?.or;

    if (!excludedFilterTypes.has('source') && sources?.length) {
      newSelectedFilters.push({ type: 'source' });
      newFiltersState.sources = sources;
    }

    const copyrights = filters.copyrightsParam?.or || [];

    if (!excludedFilterTypes.has('copyright') && copyrights?.length) {
      newSelectedFilters.push({ type: 'copyright' });
      newFiltersState.copyrights = copyrights;
    }

    const smartTags = getSmartTagsStateFromParams(filters.smartTagsParam);

    if (!excludedFilterTypes.has('smartTags') && smartTags?.smartTags?.length) {
      newSelectedFilters.push({ type: 'smartTags' });
      newFiltersState.smartTags = smartTags;
    }

    const { states, cities, countries } = getLocationStateFromParams(filters.locationParam);

    if (!excludedFilterTypes.has('country') && countries?.length) {
      newSelectedFilters.push({ type: 'country' });
      newFiltersState.countries = countries;
    }

    if (!excludedFilterTypes.has('state') && states?.length) {
      newSelectedFilters.push({ type: 'state' });
      newFiltersState.states = states;
    }

    if (!excludedFilterTypes.has('city') && cities?.length) {
      newSelectedFilters.push({ type: 'city' });
      newFiltersState.cities = cities;
    }

    const extensions = filters.extensionsParam?.or || [];

    if (!excludedFilterTypes.has('extension') && extensions?.length) {
      newSelectedFilters.push({ type: 'extension' });
      newFiltersState.extensions = extensions;
    }

    const importedKeywords = getImportedKeywordsStateFromParams(filters.importedKeywordsParam);

    if (!excludedFilterTypes.has('importedKeywords') && importedKeywords?.keywords?.length) {
      newSelectedFilters.push({ type: 'importedKeywords' });
      newFiltersState.importedKeywords = importedKeywords;
    }

    const cameras = filters.camerasParam?.or ?? [];

    if (!excludedFilterTypes.has('camera') && cameras?.length) {
      newSelectedFilters.push({ type: 'camera' });

      newFiltersState.camera = {
        make: cameras[0]?.make ?? '',
        models: cameras.map(({ model }) => model).filter(isDefined),
      };
    }

    const orientations = filters.orientationsParam;

    if (!excludedFilterTypes.has('orientation') && (orientations?.or?.length || orientations?.is)) {
      newSelectedFilters.push({ type: 'orientation' });
      newFiltersState.orientations = orientations?.or || (orientations?.is ? [orientations.is] : []);
    }

    const videoAspectRatios = filters.videoAspectRatiosParam?.or;

    if (!excludedFilterTypes.has('videoAspectRatio') && videoAspectRatios?.length) {
      newSelectedFilters.push({ type: 'videoAspectRatio' });
      newFiltersState.videoAspectRatios = videoAspectRatios;
    }

    const audioCodings = filters.audioCodingsParam?.or;

    if (!excludedFilterTypes.has('audioCoding') && audioCodings?.length) {
      newSelectedFilters.push({ type: 'audioCoding' });
      newFiltersState.audioCodings = audioCodings;
    }

    const audioSampleRates = filters.audioSampleRatesParam?.or;

    if (!excludedFilterTypes.has('audioSampleRate') && audioSampleRates?.length) {
      newSelectedFilters.push({ type: 'audioSampleRate' });
      newFiltersState.audioSampleRates = audioSampleRates.map((rate) => rate.toString());
    }

    const createdDate = getDateStateFromParams(filters.createdDateParam);

    if (!excludedFilterTypes.has('dateCreated') && createdDate) {
      newSelectedFilters.push({ type: 'dateCreated' });
      newFiltersState.createdDate = createdDate;
    }

    const videoFrameRates = filters.videoFrameRatesParam?.or;

    if (!excludedFilterTypes.has('videoFrameRate') && videoFrameRates?.length) {
      newSelectedFilters.push({ type: 'videoFrameRate' });
      newFiltersState.videoFrameRates = videoFrameRates.map((v) => v.toString());
    }

    const uploadedDate = getDateStateFromParams(filters.uploadedDateParam);

    if (!excludedFilterTypes.has('dateUploaded') && uploadedDate) {
      newSelectedFilters.push({ type: 'dateUploaded' });
      newFiltersState.uploadedDate = uploadedDate;
    }

    const modifiedDate = getDateStateFromParams(filters.updatedDateParam);

    if (!excludedFilterTypes.has('dateModified') && modifiedDate) {
      newSelectedFilters.push({ type: 'dateModified' });
      newFiltersState.modifiedDate = modifiedDate;
    }

    const takenDate = getDateStateFromParams(filters.takenDateParam);

    if (!excludedFilterTypes.has('dateTaken') && takenDate) {
      newSelectedFilters.push({ type: 'dateTaken' });
      newFiltersState.takenDate = takenDate;
    }

    const locations = getLocationsStateFromParams(filters.locationsParam);

    if (!excludedFilterTypes.has('locations') && (locations.boards.length > 0 || locations.libraries.length > 0)) {
      newSelectedFilters.push({ type: 'locations' });
      newFiltersState.locations = locations;
    }

    const customFieldsById = mergeCustomFieldsForHydration({
      lookupFields: customFieldsOptions,
      catalogFields,
    });
    let customFields = getCustomFieldsStateFromParams(filters.customFieldsParam, customFieldsById);

    const locationsParam = getLocationsStateFromParams(filters.locationsParam);

    if (locationsParam.boards.length || locationsParam.libraries.length) {
      newSelectedFilters.push({ type: 'locations' });
      newFiltersState.locations = {
        boards: locationsParam.boards,
        libraries: locationsParam.libraries,
      };
    }

    const valuesAreMissing = customFields.some((cf) => cf.values.some((value) => !value));

    if (valuesAreMissing) {
      customFields = customFields.reduce<CustomFieldSelectedFilter[]>((acc, cf) => {
        if (excludedCustomFields.has(cf.id)) {
          return acc;
        }

        const values = cf.values.filter((value) => !!value);
        const nextField = {
          ...cf,
          values,
        };

        if (values.length || customFieldFilterIsActive(nextField)) {
          acc.push(nextField);
        }

        return acc;
      }, []);
    }

    const creators = filters.creatorsParam?.or || [];

    if (!excludedFilterTypes.has('creator') && creators?.length) {
      newSelectedFilters.push({ type: 'creator' });
      newFiltersState.creators = creators;
    }

    const similarToClipId = filters.semanticClipSearchParam?.is;

    if (similarToClipId) {
      newSelectedFilters.push({ type: 'similarTo' });
      newFiltersState.similarTo = similarToClipId;
    }

    let customFieldsState: SelectedFilter[] = customFields?.map((customField) => ({
      type: 'customField',
      customField: customFieldsById[customField.id]
        ? {
            name: customFieldsById[customField.id].name,
            id: customField.id,
            type: customFieldsById[customField.id].type,
          }
        : undefined,
    }));

    const customFieldsAreMissing = customFieldsState.some((cf) => !cf.customField);

    if (customFieldsAreMissing) {
      customFields = customFields.reduce<CustomFieldSelectedFilter[]>((acc, curr) => {
        if (customFieldsById[curr.id]) {
          acc.push(curr);
        }

        return acc;
      }, []);
      customFieldsState = customFieldsState.filter((cf) => !!cf.customField);
    }

    const customFieldsForState = preserveUnresolvedPlainText
      ? customFieldsHydratedWithFailedCatalog({
          hydrated: customFields,
          urlFilters: filters.customFieldsParam,
        })
      : customFields;
    const preservedFields = preserveUnresolvedPlainText
      ? customFieldsForState.filter((field) => !customFields.some((current) => current.id === field.id))
      : [];

    if (customFieldsForState?.length) {
      newSelectedFilters.push(...customFieldsState, ...selectedFiltersForUnresolvedPlainText(preservedFields));
      newFiltersState.customFields = customFieldsForState;
    }

    // if any filter value is missing (e.g. tag has been deleted or user is on a different workspace
    // remove outdated filters from url
    if (tagsAreMissing || customFieldsAreMissing || valuesAreMissing || uploadersAreMissing || peopleAreMissing) {
      applyFilters({ filters: newFiltersState });
    }

    dispatch(setFiltersValuesAction({ filters: newFiltersState }));
    dispatch(setSelectedFiltersAction({ filters: newSelectedFilters }));
  }, [
    entities,
    options,
    store,
    filtersParams,
    immutableFilters,
    filters.tagsParam,
    filters.peopleParam,
    filters.uploadersParam,
    filters.bookmarkedParam,
    filters.hasVersionsParam,
    filters.hasOpenCommentsParam,
    filters.untaggedParam,
    filters.isOnBoardsParam,
    filters.typesParam,
    filters.assetFileKindParam,
    filters.sourcesParam?.or,
    filters.copyrightsParam?.or,
    filters.smartTagsParam,
    filters.locationParam,
    filters.extensionsParam?.or,
    filters.importedKeywordsParam,
    filters.camerasParam?.or,
    filters.orientationsParam,
    filters.videoAspectRatiosParam?.or,
    filters.audioCodingsParam?.or,
    filters.audioSampleRatesParam?.or,
    filters.createdDateParam,
    filters.videoFrameRatesParam?.or,
    filters.uploadedDateParam,
    filters.updatedDateParam,
    filters.takenDateParam,
    filters.locationsParam,
    filters.customFieldsParam,
    filters.creatorsParam?.or,
    filters.colorsParam,
    filters.semanticClipSearchParam,
    dispatch,
    applyFilters,
    catalogCustomFields,
    preserveUnresolvedPlainText,
  ]);

  return {
    setFiltersFromContextToRedux,
  };
};
